import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, MapPin, Camera, X, Clock, ShieldCheck, ShieldX } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

/**
 * Admin-only modal that shows the geolocation + silent identity-photo history
 * for a given user. The user never sees this. Photos are streamed directly
 * from the backend (raw image bytes) using the admin's Authorization header.
 */
export const UserSessionsModal = ({ open, onClose, userId, userName }) => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [photoSrcs, setPhotoSrcs] = useState({}); // photoId -> blob url
  const [bigPhoto, setBigPhoto] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${userId}/session-history`, { headers: authHeader() });
      setData(r.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [authHeader, userId]);

  useEffect(() => {
    if (open) {
      setPhotoSrcs({});
      load();
    } else {
      // Free blob URLs on close
      Object.values(photoSrcs).forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* noop */ }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const loadPhoto = useCallback(async (photoId) => {
    if (photoSrcs[photoId]) return photoSrcs[photoId];
    try {
      const r = await axios.get(
        `${API}/admin/users/${userId}/session-photo/${photoId}`,
        { headers: authHeader(), responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      setPhotoSrcs((p) => ({ ...p, [photoId]: url }));
      return url;
    } catch {
      return null;
    }
  }, [authHeader, photoSrcs, userId]);

  // Lazily load all photos when data arrives
  useEffect(() => {
    if (data?.photos?.length) {
      data.photos.slice(0, 12).forEach((p) => { loadPhoto(p.id); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-sessions-modal">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#d4af37]" />
              Sesiones de {userName || data?.username || data?.name || "usuario"}
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" /></div>
          ) : !data ? (
            <div className="py-10 text-center text-sm text-neutral-500 italic">Sin datos.</div>
          ) : (
            <div className="space-y-4">
              {/* Consent status */}
              <div className="glass-dark rounded-xl p-3 flex flex-wrap items-center gap-3 text-xs text-neutral-300">
                {data.camera_consent === true ? (
                  <span className="inline-flex items-center gap-1 text-emerald-300">
                    <ShieldCheck className="w-4 h-4" /> El usuario aceptó la captura de fotos
                  </span>
                ) : data.camera_consent === false ? (
                  <span className="inline-flex items-center gap-1 text-amber-300">
                    <ShieldX className="w-4 h-4" /> El usuario rechazó la captura
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-neutral-400">
                    <ShieldX className="w-4 h-4" /> Aún sin decisión
                  </span>
                )}
                {data.camera_consent_at && (
                  <span className="text-neutral-500">· {fmtDate(data.camera_consent_at)}</span>
                )}
                {data.last_session_photo_at && (
                  <span className="ml-auto text-neutral-400">Última foto: {fmtDate(data.last_session_photo_at)}</span>
                )}
              </div>

              {/* Photos */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Camera className="w-4 h-4 text-[#d4af37]" />
                  <span className="text-white text-sm font-serif">Fotos de verificación ({data.photos?.length || 0})</span>
                </div>
                {!data.photos?.length ? (
                  <div className="text-xs text-neutral-500 italic">Sin fotos aún.</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {data.photos.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`session-photo-${p.id}`}
                        onClick={() => setBigPhoto(p.id)}
                        className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-[#d4af37]/60 bg-black/40"
                      >
                        {photoSrcs[p.id] ? (
                          <img
                            src={photoSrcs[p.id]}
                            alt="Sesión"
                            className="w-full aspect-square object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full aspect-square flex items-center justify-center">
                            <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 px-2 py-1 bg-black/70 text-[10px] text-neutral-300">
                          {fmtDate(p.at)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Geo history */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-[#d4af37]" />
                  <span className="text-white text-sm font-serif">Ubicaciones ({data.geo_history?.length || 0})</span>
                </div>
                {!data.geo_history?.length ? (
                  <div className="text-xs text-neutral-500 italic">Sin registros.</div>
                ) : (
                  <div className="rounded-xl border border-white/10 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-black/40 text-[10px] uppercase tracking-widest text-neutral-400">
                          <th className="px-3 py-2 text-left">Cuándo</th>
                          <th className="px-3 py-2 text-left">Lat, Lng</th>
                          <th className="px-3 py-2 text-left">Precisión</th>
                          <th className="px-3 py-2 text-left">Fuente</th>
                          <th className="px-3 py-2 text-right">Mapa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(data.geo_history || [])].reverse().map((g, idx) => (
                          <tr key={idx} className="border-t border-white/5">
                            <td className="px-3 py-2 text-neutral-300 whitespace-nowrap">{fmtDate(g.at)}</td>
                            <td className="px-3 py-2 text-neutral-300 font-mono">{Number(g.lat).toFixed(5)}, {Number(g.lng).toFixed(5)}</td>
                            <td className="px-3 py-2 text-neutral-400">{g.accuracy ? `${Math.round(g.accuracy)} m` : "—"}</td>
                            <td className="px-3 py-2 text-neutral-400">{g.source || "—"}</td>
                            <td className="px-3 py-2 text-right">
                              <a
                                href={`https://www.google.com/maps?q=${g.lat},${g.lng}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-[#d4af37] hover:text-white text-[11px] uppercase tracking-widest"
                              >
                                Ver
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Big preview */}
      <Dialog open={!!bigPhoto} onOpenChange={(v) => !v && setBigPhoto(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <Camera className="w-4 h-4 text-[#d4af37]" /> Foto de verificación
              <button type="button" onClick={() => setBigPhoto(null)} className="ml-auto text-neutral-400 hover:text-white"><X className="w-4 h-4" /></button>
            </DialogTitle>
          </DialogHeader>
          {bigPhoto && photoSrcs[bigPhoto] ? (
            <img src={photoSrcs[bigPhoto]} alt="Detalle" className="w-full rounded-lg" />
          ) : (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" /></div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserSessionsModal;
